import { Button } from '@creditornot/cb-button';
import { FormRow, InputContainer } from '@creditornot/cb-form';
import { Input, Textarea } from '@creditornot/cb-input';
import { ModalFooter, ModalHeader, ModalContent } from '@creditornot/cb-modal';
import React, { useCallback } from 'react';
import { Field, useForm } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { VenueGroup } from 'apis/venue-groups/types';
import { RFFFieldErrorWrapper } from 'common/components';

export type FormValues = {
  venueGroupName: string;
  description?: string;
};

export type Props = {
  venueGroup?: VenueGroup;
  onClick?: () => void;
  onClose: () => void;
};

const Buttons = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const StyledFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
`;

export const AddEditVenueGroup = ({ venueGroup, onClick, onClose }: Props) => {
  const intl = useIntl();
  const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);
  const { getState } = useForm();

  const { hasValidationErrors, submitting, pristine } = getState();

  const isEdit = !!venueGroup;

  return (
    <>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id={isEdit ? 'venue-groups.form.edit-venue-group' : 'venue-groups.form.create-venue-group'} />
      </ModalHeader>

      <ModalContent>
        <FormRow label={t('venue-groups.form.venue-group-name')} labelHtmlFor="venueGroupName" required>
          <Field<FormValues['venueGroupName']> name="venueGroupName">
            {field => (
              <InputContainer labelHtmlFor="venueGroupName">
                <RFFFieldErrorWrapper field={field}>
                  {input => (
                    <Input
                      {...input}
                      placeholder={t('venue-groups.form.venue-group-name-placeholder')}
                      id="venueGroupName"
                    />
                  )}
                </RFFFieldErrorWrapper>
              </InputContainer>
            )}
          </Field>
        </FormRow>

        <FormRow label={t('venue-groups.form.description')} labelHtmlFor="description">
          <Field<FormValues['description']> name="description">
            {field => (
              <InputContainer labelHtmlFor="description">
                <RFFFieldErrorWrapper field={field}>
                  {input => (
                    <Textarea
                      {...input}
                      placeholder={t('venue-groups.form.description-placeholder')}
                      id="description"
                    />
                  )}
                </RFFFieldErrorWrapper>
              </InputContainer>
            )}
          </Field>
        </FormRow>
      </ModalContent>

      <StyledFooter>
        <Buttons>
          <Button variant="lightBlue" size="small" onClick={onClose}>
            <FormattedMessage id="state.cancel" />
          </Button>

          {!isEdit && (
            <Button disabled={hasValidationErrors} onClick={onClick} variant="blue" size="small">
              <FormattedMessage id="venue-groups.form.add-venues" />
            </Button>
          )}

          {isEdit && (
            <Button
              type="submit"
              loading={submitting}
              disabled={pristine || submitting || hasValidationErrors}
              variant="blue"
              size="small"
            >
              <FormattedMessage id="state.save" />
            </Button>
          )}
        </Buttons>
      </StyledFooter>
    </>
  );
};
